import {
    closestCenter,
    DndContext,
    DragEndEvent,
    MouseSensor,
    TouchSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { App, Flex } from "antd";
import { useMemo } from "react";
import { getApiError } from "tm-shared/api";
import { Point, Schedule, UserRole } from "tm-shared/types";

import { useAddUnscheduledVisits } from "@/domains/point";
import { ScheduleCard, ScheduleMenuConfig } from "@/domains/route";
import {
    useAppDispatch,
    useAppSelector,
    useProtectedUserRoleSelector,
} from "@/hooks/redux";
import { setCurrentScheduleList } from "@/store/slices/manager";

export interface ScheduleListProps {
    displaySchedules: Schedule[];
    multiSelectMode: boolean;
    selectedSchedules: Schedule[];
    handleSelectSchedule: (item: Schedule) => void;
    handleTap: () => void;
    dayFilter: string;
    scheduleMenuConfig: ScheduleMenuConfig[];
    points: Record<string, Point>;
}

export const ScheduleList = ({
    displaySchedules,
    multiSelectMode,
    selectedSchedules,
    handleSelectSchedule,
    handleTap,
    scheduleMenuConfig,
    dayFilter,
    points,
}: ScheduleListProps) => {
    const { notification } = App.useApp();

    const dispatch = useAppDispatch();
    const currentScheduleList = useAppSelector(
        (state) => state.manager.currentScheduleList,
    );
    const role = useProtectedUserRoleSelector();
    const pointIds = useAppSelector((state) => state.work.pointIds);
    const pointExtraIds = useAppSelector((state) => state.work.pointExtraIds);
    const isUnscheduledPointsProcessing = useAppSelector(
        (state) => state.offline.isUnscheduledPointsProcessing,
    );

    const selectedRouteIds = useMemo(
        () => new Set(selectedSchedules.map((r) => r.pointId)),
        [selectedSchedules],
    );

    const sensors = useSensors(
        useSensor(MouseSensor, {
            activationConstraint: { distance: 5 },
        }),
        useSensor(TouchSensor, {
            activationConstraint: { delay: 100, tolerance: 5 },
        }),
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIndex = displaySchedules.findIndex(
            (s) => s.pointId === active.id,
        );
        const newIndex = displaySchedules.findIndex(
            (s) => s.pointId === over.id,
        );

        const reorderedDisplay = arrayMove(
            displaySchedules,
            oldIndex,
            newIndex,
        );
        const selectedDay = dayFilter ?? "0";

        if (selectedDay === "0") {
            const sortOrderMap = new Map(
                reorderedDisplay.map((item, index) => [
                    item.pointId,
                    index + 1,
                ]),
            );

            const finalList = [...currentScheduleList]
                .map((item) => ({
                    ...item,
                    sortOrder: sortOrderMap.get(item.pointId) ?? item.sortOrder,
                }))
                .sort((a, b) => a.sortOrder - b.sortOrder);

            dispatch(setCurrentScheduleList(finalList));
            return;
        }

        const selectedDayNumber = Number(selectedDay);

        const reorderedDayMap = new Map(
            reorderedDisplay.map((item, index) => [item.pointId, index]),
        );

        const matched = currentScheduleList.filter((item) =>
            item.days.includes(selectedDayNumber),
        );

        const unmatched = currentScheduleList.filter(
            (item) => !item.days.includes(selectedDayNumber),
        );

        const reorderedMatched = [...matched].sort(
            (a, b) =>
                (reorderedDayMap.get(a.pointId) ?? 0) -
                (reorderedDayMap.get(b.pointId) ?? 0),
        );

        const finalList = [...unmatched, ...reorderedMatched].map(
            (item, index) => ({ ...item, sortOrder: index + 1 }),
        );

        dispatch(setCurrentScheduleList(finalList));
    };

    const { addUnscheduledVisits } = useAddUnscheduledVisits();

    const unplannedMenuItem = useMemo<ScheduleMenuConfig | null>(() => {
        if (role !== UserRole.Kam) return null;

        return {
            type: "addUnplanned",
            label: "Додати на сьогодні",
            onClick: (item: Schedule) => {
                try {
                    addUnscheduledVisits({
                        pointIds: [item.pointId],
                    });
                } catch (error) {
                    const { displayMessage } = getApiError(error);
                    notification.error({
                        title: "Не вдалося додати позапланову точку",
                        description: displayMessage,
                    });
                }
            },
            disabled: () => isUnscheduledPointsProcessing,
        };
    }, [
        notification,
        role,
        isUnscheduledPointsProcessing,
        addUnscheduledVisits,
    ]);

    const pointIdsSet = useMemo(
        () => new Set([...pointIds, ...pointExtraIds]),
        [pointIds, pointExtraIds],
    );

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
        >
            <SortableContext
                items={displaySchedules.map((s) => s.pointId)}
                strategy={verticalListSortingStrategy}
            >
                <Flex vertical gap={8}>
                    {displaySchedules.map((item) => {
                        const { pointId } = item;

                        const point = points[pointId];
                        const isSelected = selectedRouteIds.has(pointId);

                        const canAddUnplanned = !pointIdsSet.has(pointId);

                        const menu =
                            canAddUnplanned && unplannedMenuItem
                                ? [unplannedMenuItem, ...scheduleMenuConfig]
                                : scheduleMenuConfig;

                        return (
                            <ScheduleCard
                                key={item.pointId}
                                schedule={item}
                                point={point}
                                isSelected={isSelected}
                                disableDrag={multiSelectMode}
                                hideMenu={multiSelectMode}
                                onClick={handleSelectSchedule}
                                onTouchStart={handleTap}
                                scheduleMenuConfig={menu}
                            />
                        );
                    })}
                </Flex>
            </SortableContext>
        </DndContext>
    );
};
